import { fetchItems } from '../../components/ProductsProvider/ProductsProvider'
import { message } from 'antd'
import { warehousesDictionary, warehousesForApparel } from '../../constants'

const calculateParent = SKU => {
  // Handle empty or invalid SKUs
  if (!SKU || typeof SKU !== 'string') {
    message.error(`Invalid SKU: ${SKU}`)
    return null
  }

  const upperSKU = SKU.toUpperCase()

  // Shoes (numeric SKUs): First 4 digits
  // Men's shoes: 1234567 → 1234
  // Women's shoes: D1234567 → D1234
  if (/^\d/.test(upperSKU)) {
    return upperSKU.slice(0, 4)
  }

  if (/^D\d/.test(upperSKU)) {
    return upperSKU.slice(0, 5) // D + 4 digits
  }

  // Check for clothing patterns FIRST (more specific than accessories)
  const menClothingPrefixes = ['ABT', 'CMC', 'CSP', 'MGN', 'PNT', 'TSR', 'FLP']
  const womenClothingPrefixes = ['DCS', 'DCSP', 'DMGN', 'DPNT']

  // Men's clothing: 3 letters + 3 digits = 6 chars
  for (const prefix of menClothingPrefixes) {
    if (upperSKU.startsWith(prefix)) {
      return upperSKU.slice(0, 6)
    }
  }

  // Women's clothing: D + 2-3 letters + 3 digits = 6-7 chars
  // Sort by length (longest first) to avoid conflicts like DCS vs DCSP
  const sortedWomenPrefixes = womenClothingPrefixes.sort(
    (a, b) => b.length - a.length,
  )
  for (const prefix of sortedWomenPrefixes) {
    if (upperSKU.startsWith(prefix)) {
      return upperSKU.slice(0, prefix.length + 3) // prefix length + 3 digits
    }
  }

  // Accessories - defined prefixes with variable lengths
  const accessoryPrefixes = {
    // Men's accessories
    A: 4, // A040
    B: 4, // B040
    C: 4, // C040
    CA: 5, // CA040
    CPL: 6, // CPL040
    CR: 5, // CR040
    CS: 5, // CS040
    P: 4, // P040
    PCT: 6, // PCT040
    PF: 5, // PF040
    SCP: 7, // SCP0025
    // Women's accessories (D prefix)
    DA: 5, // DA040
    DB: 5, // DB040
    DC: 5, // DC030
    DCA: 6, // DCA040
    DCPL: 7, // DCPL040
    DK: 5, // DK040
    DP: 5, // DP040
    DSCP: 8, // DSCP0006
  }

  // Check for accessory prefixes (longest first to avoid conflicts)
  const sortedPrefixes = Object.keys(accessoryPrefixes).sort(
    (a, b) => b.length - a.length,
  )

  for (const prefix of sortedPrefixes) {
    if (upperSKU.startsWith(prefix)) {
      return upperSKU.slice(0, accessoryPrefixes[prefix])
    }
  }

  message.error(`Couldn't calculate parent for SKU: ${SKU}`)

  return null
}

export const getProductsBySKU = async (targetSKU, token, stores = []) => {
  try {
    // Check if stores are loaded
    if (!stores || stores.length === 0) {
      message.warn(
        'getItemBySKU called with empty stores array. Stores may not be loaded yet.',
      )
      return null
    }

    const storeIds = []

    storeIds.push(
      ...warehousesDictionary.fiege,
      ...warehousesDictionary.fli,
      ...Object.keys(warehousesForApparel),
    )

    stores.forEach(store => {
      // Add the main store ID
      storeIds.push(store.id)

      // Add all sublocation IDs if they exist
      if (
        store.values?.sublocations &&
        Array.isArray(store.values.sublocations)
      ) {
        store.values.sublocations.forEach(sublocation => {
          storeIds.push(sublocation.id)
        })
      }
    })

    const deduplicatedStoreIds = [...new Set(storeIds)]

    const productParent = calculateParent(targetSKU)

    const fetchWithPagination = async () => {
      if (!productParent) {
        return null
      }

      const products = await fetchItems({
        store: deduplicatedStoreIds,
        token,
        brands: [1, 2, 3, 4],
        productParent,
      })

      const foundItems = products.filter(
        item => item.custitem_velprod_item_name === targetSKU,
      )

      if (foundItems.length > 0) {
        return foundItems
      }

      return null
    }

    // Create a timeout promise that rejects after 30 seconds
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Fetch operation timed out after 30 seconds'))
      }, 30 * 1000)
    })

    try {
      // Race between the fetch operation and timeout
      return await Promise.race([fetchWithPagination(), timeoutPromise])
    } catch (error) {
      if (error.message.includes('timed out')) {
        message.error(
          'Search operation timed out after 30 seconds. Please try again.',
        )
        return null
      }
      throw error // Re-throw other errors to be handled by the outer catch
    }
  } catch (error) {
    console.error('Error fetching item by SKU:', error)
    return null
  }
}
